import { FC, useEffect, useRef } from 'react';
import { useAuthContext } from 'auth';
import { useModalContext } from 'store/modal';
import { useSignout } from 'store/auth';
import { useMe } from 'state/Administrators';
import { SessionTimeoutModal } from './atoms';

const WARNING_TIME = 1 * 60 * 1000;
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'wheel',
  'touchstart',
  'pointerdown',
] as const;

export const SessionInactivityListener: FC = () => {
  const { loggedIn } = useAuthContext();
  const { openModal } = useModalContext();
  const { me } = useMe();

  const phaseRef = useRef<'idle' | 'warning' | 'signingOut'>('idle');
  const idleStartedAtRef = useRef(0);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const signoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { mutate: signout } = useSignout();

  useEffect(() => {
    const clearTimers = () => {
      if (warningTimeoutRef.current !== null) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      if (signoutTimeoutRef.current !== null) {
        clearTimeout(signoutTimeoutRef.current);
        signoutTimeoutRef.current = null;
      }
    };

    phaseRef.current = 'idle';

    if (!loggedIn || !me?.parentCompany?.settings.sessionTimeoutEnabled) {
      clearTimers();
      return;
    }

    const SIGNOUT_DELAY =
      me.parentCompany.settings.sessionTimeoutInMinutes * 60 * 1000;

    let cancelled = false;

    const onWarning = async () => {
      if (cancelled || phaseRef.current !== 'idle') return;

      phaseRef.current = 'warning';
      const result = await openModal({
        component: SessionTimeoutModal,
        props: { expiresAt: idleStartedAtRef.current + SIGNOUT_DELAY },
        closable: false,
      });
      phaseRef.current = 'idle';

      if (result.action === 'CONFIRM') {
        startCycle();
      }
    };

    const onSignout = () => {
      if (cancelled || phaseRef.current === 'signingOut') return;

      phaseRef.current = 'signingOut';
      clearTimers();
      return signout();
    };

    const startCycle = () => {
      if (cancelled || phaseRef.current === 'signingOut') return;

      clearTimers();
      idleStartedAtRef.current = Date.now();

      warningTimeoutRef.current = setTimeout(
        onWarning,
        SIGNOUT_DELAY - WARNING_TIME,
      );
      signoutTimeoutRef.current = setTimeout(onSignout, SIGNOUT_DELAY);
    };

    const onActivity = () => {
      if (phaseRef.current !== 'idle') return;
      startCycle();
    };

    const abortController = new AbortController();

    ACTIVITY_EVENTS.forEach((name) => {
      document.addEventListener(name, onActivity, {
        passive: true,
        signal: abortController.signal,
      });
    });

    startCycle();

    return () => {
      cancelled = true;
      abortController.abort();
      clearTimers();
      phaseRef.current = 'idle';
    };
  }, [loggedIn, me, openModal, signout]);

  return null;
};
